'use client'

import {
    createReportAutomation,
    ReportAutomation,
    ReportAutomationFormat,
    ReportAutomationFrequency,
    ReportAutomationPeriod,
    updateReportAutomation,
} from '@/lib/api/reports/report-automation-api'
import { ReportFilters } from '@/lib/api/reports/reports-api'
import type { ServiceLabels } from '@/lib/form-state/util'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useState } from 'react'
import { MdClose, MdFilterList, MdInfoOutline } from 'react-icons/md'
import { toast } from 'react-toastify'
import {
    describeReportFilters,
    extractAutomationFilters,
} from '../describe-filters'

interface Props {
    initial: ReportAutomation | null
    onClose: () => void
    onSaved: () => void
    /**
     * Filtros da tela de onde a automação foi criada. É assim que a automação
     * herda o recorte que o operador já montou — antes era preciso escrever o
     * JSON equivalente à mão, o que ninguém tinha como acertar.
     */
    presetFilters?: ReportFilters
    /** Tipo do relatório de origem. Fixa o seletor: os filtros são daquele tipo. */
    presetReportType?: string
}

/** Tipos de relatório no vocabulário do segmento (Procedimento vira Pedido fora da saúde). */
function buildReportTypes(
    labels: ServiceLabels
): { value: string; label: string }[] {
    return [
        { value: 'consultations', label: labels.Consultations },
        { value: 'orders', label: labels.Orders },
        { value: 'exams', label: labels.Exams },
        { value: 'admissions', label: 'Internações' },
        { value: 'chats', label: 'Atendimentos' },
        { value: 'billings', label: 'Lançamentos' },
    ]
}

const FREQUENCIES: { value: ReportAutomationFrequency; label: string }[] = [
    { value: 'DAILY', label: 'Diário' },
    { value: 'WEEKLY', label: 'Semanal' },
    { value: 'MONTHLY', label: 'Mensal' },
]

const PERIODS: { value: ReportAutomationPeriod; label: string }[] = [
    { value: 'TODAY', label: 'Hoje' },
    { value: 'YESTERDAY', label: 'Ontem' },
    { value: 'LAST_7_DAYS', label: 'Últimos 7 dias' },
    { value: 'LAST_30_DAYS', label: 'Últimos 30 dias' },
    { value: 'THIS_WEEK', label: 'Esta semana' },
    { value: 'LAST_WEEK', label: 'Semana passada' },
    { value: 'THIS_MONTH', label: 'Este mês' },
    { value: 'LAST_MONTH', label: 'Mês passado' },
    { value: 'THIS_YEAR', label: 'Este ano' },
]

const FORMATS: { value: ReportAutomationFormat; label: string }[] = [
    { value: 'PDF', label: 'PDF' },
    { value: 'XLSX', label: 'Excel (XLSX)' },
    { value: 'CSV', label: 'CSV' },
]

const DAYS_OF_WEEK = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
]

export default function AutomationFormModal({
    initial,
    onClose,
    onSaved,
    presetFilters,
    presetReportType,
}: Props) {
    const reportTypes = buildReportTypes(useServiceLabel())
    // Nome sugerido quando a automação nasce de um relatório: o operador quase sempre
    // repetiria o nome da aba, e um campo obrigatório vazio trava o fluxo à toa.
    const [name, setName] = useState(
        initial?.name ||
            (presetReportType
                ? (reportTypes.find(t => t.value === presetReportType)?.label ??
                  '')
                : ''),
    )
    const [reportType, setReportType] = useState(
        initial?.reportType || presetReportType || 'consultations',
    )
    const [frequency, setFrequency] = useState<ReportAutomationFrequency>(
        initial?.frequency || 'DAILY',
    )
    const [hourOfDay, setHourOfDay] = useState(initial?.hourOfDay ?? 8)
    const [dayOfWeek, setDayOfWeek] = useState(initial?.dayOfWeek ?? 1)
    const [dayOfMonth, setDayOfMonth] = useState(initial?.dayOfMonth ?? 1)
    const [periodWindow, setPeriodWindow] = useState<ReportAutomationPeriod>(
        initial?.periodWindow || 'LAST_7_DAYS',
    )
    const [format, setFormat] = useState<ReportAutomationFormat>(
        initial?.format || 'PDF',
    )
    const [emailsText, setEmailsText] = useState(
        (initial?.recipients || []).join(', '),
    )
    /**
     * Filtros gravados na automação. Não são editáveis aqui: vêm da tela do
     * relatório, onde existem seletores de verdade (parceiro, catálogo, status).
     * O que o modal oferece é ver o que foi herdado e poder limpar.
     */
    const [filters, setFilters] = useState<Record<string, any>>(
        presetFilters
            ? extractAutomationFilters(presetFilters)
            : ((initial?.filtersJson as Record<string, any>) ?? {}),
    )
    const filterSummary = describeReportFilters(reportType, filters)
    const [active, setActive] = useState(initial?.active ?? true)
    const [saving, setSaving] = useState(false)

    const handleSubmit = async () => {
        if (!name.trim()) return toast.warning('Informe um nome')
        const emails = emailsText
            .split(/[,;\n]/)
            .map((e) => e.trim())
            .filter((e) => e.length > 0 && e.includes('@'))
        if (emails.length === 0) {
            return toast.warning('Informe pelo menos um email destinatário válido')
        }

        // Objeto vazio vira `undefined`: gravar `{}` faria o backend tratar como
        // "há filtros" e o `LEGACY_DATE_BASIS` deixaria de completar a base do
        // período nas automações antigas.
        const filtersJson = Object.keys(filters).length > 0 ? filters : undefined

        const payload = {
            name: name.trim(),
            reportType,
            frequency,
            hourOfDay,
            dayOfWeek: frequency === 'WEEKLY' ? dayOfWeek : undefined,
            dayOfMonth: frequency === 'MONTHLY' ? dayOfMonth : undefined,
            periodWindow,
            format,
            recipients: emails,
            filtersJson,
            active,
        }

        setSaving(true)
        try {
            const res = initial
                ? await updateReportAutomation(initial.id, payload)
                : await createReportAutomation(payload)
            if (res.success) {
                toast.success(initial ? 'Automação atualizada' : 'Automação criada')
                onSaved()
            } else {
                toast.error(res.error || 'Erro ao salvar')
            }
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog open className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">
                        {initial ? 'Editar automação' : 'Nova automação'}
                    </h3>
                    <button
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onClose}
                    >
                        <MdClose />
                    </button>
                </div>

                <div className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">Nome *</span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex: Relatório semanal de consultas"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo de relatório *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={reportType}
                                onChange={(e) => setReportType(e.target.value)}
                                // Criada a partir de um relatório: o tipo vem junto com
                                // os filtros herdados, e trocá-lo deixaria filtros de um
                                // tipo aplicados a outro.
                                disabled={!!presetFilters}
                            >
                                {reportTypes.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Formato *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={format}
                                onChange={(e) => setFormat(e.target.value as ReportAutomationFormat)}
                            >
                                {FORMATS.map((f) => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">Período do relatório *</span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={periodWindow}
                            onChange={(e) => setPeriodWindow(e.target.value as ReportAutomationPeriod)}
                        >
                            {PERIODS.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-base-content/60 mt-1">
                            A janela é calculada na hora de gerar o relatório.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Frequência *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={frequency}
                                onChange={(e) => setFrequency(e.target.value as ReportAutomationFrequency)}
                            >
                                {FREQUENCIES.map((f) => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {frequency === 'WEEKLY' && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Dia da semana *</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={dayOfWeek}
                                    onChange={(e) => setDayOfWeek(Number(e.target.value))}
                                >
                                    {DAYS_OF_WEEK.map((d) => (
                                        <option key={d.value} value={d.value}>
                                            {d.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {frequency === 'MONTHLY' && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Dia do mês *</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={dayOfMonth}
                                    onChange={(e) => setDayOfMonth(Number(e.target.value))}
                                >
                                    {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                                        <option key={d} value={d}>
                                            Dia {d}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Hora *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={hourOfDay}
                                onChange={(e) => setHourOfDay(Number(e.target.value))}
                            >
                                {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                                    <option key={h} value={h}>
                                        {String(h).padStart(2, '0')}:00
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-control w-full">
                        <label className="label">
                            <span className="label-text font-medium">Destinatários *</span>
                        </label>
                        <textarea
                            className="textarea textarea-bordered w-full"
                            rows={2}
                            placeholder="email@empresa.com, outro@empresa.com"
                            value={emailsText}
                            onChange={(e) => setEmailsText(e.target.value)}
                        />
                        <p className="text-xs text-base-content/60 mt-1">
                            Separe múltiplos emails por vírgula ou ponto-e-vírgula.
                        </p>
                    </div>

                    <div className="rounded-lg border border-base-300 p-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-sm font-medium flex items-center gap-2">
                                <MdFilterList className="text-secondary" />
                                Filtros do relatório
                            </span>
                            {filterSummary.length > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-xs btn-ghost"
                                    onClick={() => setFilters({})}
                                >
                                    Limpar
                                </button>
                            )}
                        </div>

                        {filterSummary.length > 0 ? (
                            <>
                                <div className="flex flex-wrap gap-1.5">
                                    {filterSummary.map((entry) => (
                                        <span
                                            key={entry.label}
                                            className="badge badge-sm bg-secondary/10 text-secondary border-0 h-auto py-1"
                                        >
                                            {entry.label}: {entry.value}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-xs text-base-content/60 mt-2">
                                    Herdados da tela do relatório. O período não entra
                                    aqui — quem define a janela é o campo acima, e ele é
                                    recalculado a cada envio.
                                </p>
                            </>
                        ) : (
                            <p className="text-xs text-base-content/60 flex items-start gap-1.5">
                                <MdInfoOutline className="text-base shrink-0 mt-0.5" />
                                Sem filtros: a automação envia o relatório completo do
                                período. Para filtrar, monte o recorte na tela do
                                relatório e use o botão "Automatizar" de lá.
                            </p>
                        )}
                    </div>

                    <div className="form-control">
                        <label className="label cursor-pointer justify-start gap-3">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary"
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                            />
                            <span className="label-text font-medium">
                                Automação ativa
                            </span>
                        </label>
                    </div>
                </div>

                <div className="modal-action">
                    <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleSubmit}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : initial ? (
                            'Salvar'
                        ) : (
                            'Criar automação'
                        )}
                    </button>
                </div>
            </div>
            <div
                className="modal-backdrop"
                onClick={() => !saving && onClose()}
            />
        </dialog>
    )
}
